// Tiny analytics wrapper. Pushes to GTM dataLayer (GTM is hardcoded in
// index.html — no env-var-driven injection here). Logs to the console in
// dev so you can see events fire without a network tab.
//
// Two surfaces:
//   1. track(event, props) — generic internal funnel events. Cheap, untyped
//      key/value pushes. Used in 14 places across the funnel.
//   2. trackQuizStart / trackQuizComplete / trackPurchase — the three
//      jobtest.org GTM doc-specified events. Match the spec exactly: a
//      `userFlow` field plus a nested `user` object with id / email /
//      phoneNumber / zipCode.

import { useQuizStore } from '../store/quizStore';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type TrackEvent =
  | 'quiz_started'
  | 'question_answered'
  | 'quiz_completed'
  | 'lead_captured'
  | 'building_report_viewed'
  | 'report_ready_clicked'
  | 'pricing_viewed'
  | 'pricing_welcome_back'
  | 'hero_cta_clicked'
  | 'plan_selected'
  | 'checkout_viewed'
  | 'checkout_addon_choice'
  | 'checkout_submitted'
  | 'checkout_completed'
  | 'report_ready_auto_advanced'
  | 'ic_upsell_viewed'
  | 'ic_upsell_clicked'
  | 'ic_upsell_declined'
  | 'ic_trial_clicked'
  | 'retake_started'
  | 'retake_quiz_completed'
  | 'retake_report_ready';

function push(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[analytics]', payload);
  }
}

export function track(event: TrackEvent, props: Record<string, unknown> = {}): void {
  push({ event, ...props });
}

// ---------- jobtest.org GTM doc-specified events ----------
//
// The Stape container expects user identity bundled inside a `user` object.
// Fields are intentionally always present (empty string when unknown) so
// downstream tags can pick them consistently without optional-chaining.

interface UserBlock {
  id: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
}

/**
 * Build the `user` block from the Zustand store. Reads:
 *   - leadId       → user.id
 *   - leadEmail    → user.email
 *   - leadPhone    → user.phoneNumber
 *   - variables.zipCode (if present) → user.zipCode
 *
 * All fields fall back to '' when missing — never null/undefined — so the
 * pushed shape is stable.
 */
function userBlock(): UserBlock {
  const s = useQuizStore.getState();
  // zipCode isn't currently captured by the quiz; left as '' so the field
  // is present-but-empty. If a zip question gets added, point this at the
  // matching variable key.
  const zip = s.variables.zipCode ?? s.variables.zip ?? '';
  return {
    id: s.leadId ?? '',
    email: s.leadEmail ?? '',
    phoneNumber: s.leadPhone ?? '',
    zipCode: zip,
  };
}

/**
 * Fire a `pageView` on every route change (wired in App.tsx → PageViewTracker).
 * Carries the same `user` identity block as the other spec'd events so the
 * Stape container can attribute the view to a known lead once one exists
 * (fields are present-but-empty before lead capture). zipCode is always ''
 * today — the quiz doesn't collect one.
 */
export function trackPageView(): void {
  push({
    event: 'pageView',
    user: userBlock(),
  });
}

export function trackQuizStart(): void {
  push({
    event: 'quizStart',
    userFlow: 'report',
    user: userBlock(),
  });
}

export function trackQuizComplete(): void {
  push({
    event: 'quizComplete',
    userFlow: 'report',
    user: userBlock(),
  });
}

interface PurchaseArgs {
  /** USD price actually charged for the plan, e.g. 29.99. */
  value: number;
  /** Stripe checkout session id (cs_…). Falls back to '' if unknown. */
  transactionId: string;
}

export function trackPurchase(args: PurchaseArgs): void {
  push({
    event: 'purchase',
    userFlow: 'report',
    value: args.value,
    currency: 'USD',
    transactionId: args.transactionId,
    user: userBlock(),
  });
}
